import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactPlayer from "react-player";
import { Modal, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { AppDispatch, RootState } from "../../store";
import { useVideoPlayer } from "../../hooks/Videos/useVideoPlayer";
import { useHlsMedia } from "../../hooks/Videos/useHlsStreaming";
import { useGetProfilesByIdQuery, useUpdateProfileMutation } from "../../api/modules";
import { setUser } from "../../store/reducer";
import Keys from "../../constants/helper";
import { Loader } from "../index";
import VideoPlayerControls from "./VideoPlayerControls";
import PauseOverlay from "./PauseOverlay";
import EventNotStartedOverlay from "./EventNotStartedOverlay";
import MultiViewSlot from "./MultiViewSlot";
import { useIosFullscreen } from "./hooks/useIosFullscreen";
import { PlayerConfig, isRealtimeContentType, isIOSIPhone, getVideoElement } from "./videoPlayer.utils";
import { ProgramPages } from "../../types";
import { routes } from "../../routes/routes";
import { getProfileLanguageFromTrack } from "../../utils";
import { addMultiViewSlot, IMultiViewSlot } from "../../store/player/playerSlice";
import styles from "./VideoPlayer.module.css";

const VideoPlayer: React.FC = () => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation("translations");
    const dispatch: AppDispatch = useDispatch();
    const isIOSIPhoneRef = useRef(isIOSIPhone());
    const activeProfile = useSelector((state: RootState) => state.user.user);
    const storedProfileId = localStorage.getItem(Keys.EPIC_PROFILE_ID) ?? undefined;
    const profileId = activeProfile?.id ?? storedProfileId;
    const profileQuery = useGetProfilesByIdQuery(profileId, {
        skip: !profileId,
    });
    const currentProfile = profileQuery?.data;
    const profileLanguage = currentProfile?.language ?? activeProfile?.language;
    const updateProfileTuple = useUpdateProfileMutation() || [];
    const updateProfile = updateProfileTuple[0];

    const {
        id,
        play,
        type,
        videoData,
        isVideoPlayerOpen,
        eventNotStartedOverlay,
        contentContext,
        multiViewSlots = [],
    } = useSelector((state: RootState) => state.videoPlayer);

    const isLiveType = isRealtimeContentType(type);
    const isSportsContext = contentContext?.page === ProgramPages.SPORTS || type === ProgramPages.SPORTS;
    const isEventNotStartedVisible = eventNotStartedOverlay?.visible;
    const playerUrl = isEventNotStartedVisible ? undefined : (videoData?.url || undefined);
    const shouldEnableTapToggle = !isLiveType && type !== "SPORTS";

    const isSportsProgram = (type === "SPORTS" || contentContext?.page === ProgramPages.SPORTS || (multiViewSlots.length > 0 && multiViewSlots[0]?.type === "SPORTS")) && Boolean(multiViewSlots && multiViewSlots.length > 0);

    const isMultiViewActive = useMemo(() => {
        if (!multiViewSlots || multiViewSlots.length === 0) return false;
        return Boolean(isSportsProgram);
    }, [isSportsProgram, multiViewSlots]);

    const {
        playerRef,
        containerRef,
        loading,
        setLoading,
        iosExitSeekRef,
        handleReady: handleReadyFromHook,
        togglePlayState,
        handleProgress,
        handleDuration,
        handleSeek,
        toggleFullscreen,
        handleClose,
        liveReloadNonce,
    } = useVideoPlayer(id, type, isVideoPlayerOpen);

    const {
        availableLanguages,
        availableSubtitles,
        selectedLanguage,
        selectedSubtitle,
        subtitlesEnabled,
        subtitlesEnabledRef,
        selectedSubtitleRef,
        selectedLanguageRef,
        audioOrderRef,
        syncSubtitlesEnabled,
        syncAudioLang,
        handleHls,
        handleLanguageChange,
        handleSubtitleChange,
        handleSubtitleToggle,
        destroyHls,
    } = useHlsMedia(playerRef, {
        preferredLanguage: profileLanguage,
    });

    const [muted, setMuted] = useState(true);

    useEffect(() => {
        if (currentProfile) dispatch(setUser(currentProfile));
    }, [currentProfile, dispatch]);

    useEffect(() => {
        if (profileLanguage && i18n.language !== profileLanguage) {
            void i18n.changeLanguage(profileLanguage);
        }
    }, [i18n, profileLanguage]);

    const initHls = useCallback(() => {
        if (playerUrl?.includes(".m3u8")) {
            handleHls(playerUrl, { isLiveContent: isLiveType });
        }
    }, [playerUrl, handleHls, isLiveType]);

    const { attach: attachIosFullscreen } = useIosFullscreen({
        playerRef,
        playerUrl,
        isLiveType,
        destroyHls,
        handleHls,
        subtitlesEnabledRef,
        selectedSubtitleRef,
        selectedAudioLangRef: selectedLanguageRef,
        audioOrderRef,
        syncSubtitlesEnabled,
        syncAudioLang,
        iosExitSeekRef,
    });

    const handleReady = useCallback(() => {
        initHls();
        handleReadyFromHook();

        const video = getVideoElement(playerRef);
        if (!video) return;

        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");
        video.setAttribute("x-webkit-airplay", "allow");
        video.setAttribute("airplay", "allow");
        (video as any).disableRemotePlayback = false;
        video.removeAttribute("disableremoteplayback");
        video.autoplay = true;

        attachIosFullscreen(video);

        const exitTime = iosExitSeekRef.current;
        if (exitTime !== null && exitTime > 1) {
            iosExitSeekRef.current = null;
            playerRef.current?.seekTo(exitTime, "seconds");
        }
    }, [initHls, handleReadyFromHook, playerRef, attachIosFullscreen, iosExitSeekRef]);

    useEffect(() => {
        if (play) setMuted(false);
    }, [play]);

    const handleBack = async () => {
        await handleClose();
        navigate(routes.sports, { replace: true });
    };

    const handleAirPlay = useCallback(() => {
        const video = getVideoElement(playerRef) as HTMLVideoElement & {
            webkitShowPlaybackTargetPicker?: () => void;
        };
        if (video?.webkitShowPlaybackTargetPicker) {
            video.webkitShowPlaybackTargetPicker();
        }
    }, [playerRef]);

    const handlePersistedLanguageChange = useCallback(
        (event: any) => {
            const audioLanguage = event.target.value as string;
            handleLanguageChange(event);

            const profileLanguage = getProfileLanguageFromTrack(audioLanguage);
            if (!profileId || !profileLanguage || profileLanguage === activeProfile?.language) return;

            const optimisticProfile = {
                ...(activeProfile ?? {}),
                language: profileLanguage,
            };
            dispatch(setUser(optimisticProfile));
            void i18n.changeLanguage(profileLanguage);

            void updateProfile({ id: profileId, body: { language: profileLanguage } })
                .unwrap()
                .then((response) => {
                    if (response) {
                        dispatch(setUser(response));
                        if (response.language) void i18n.changeLanguage(response.language);
                    }
                })
                .catch(() => void 0);
        },
        [activeProfile, dispatch, handleLanguageChange, i18n, profileId, updateProfile],
    );

    const handleAddSlot = () => {
        dispatch(addMultiViewSlot());
    };

    const gridClass = useMemo(() => {
        const count = multiViewSlots.length;
        if (count === 2) return styles.grid2Events;
        if (count === 3) return styles.grid3Events;
        if (count >= 4) return styles.grid4Events;
        return styles.grid1Event;
    }, [multiViewSlots.length]);

    return (
        <Modal
            open={isVideoPlayerOpen}
            onClose={handleClose}
            closeAfterTransition={false}
            className={styles.modal}
            disableScrollLock
        >
            <div ref={containerRef} className={styles.modalContent}>
                {isMultiViewActive ? (
                    <Box className={styles.multiViewContainer}>
                        <Box className={clsx(styles.multiViewGrid, gridClass)}>
                            {multiViewSlots.map((slot, idx) => (
                                <MultiViewSlot
                                    key={slot.id ? `${slot.id}-${idx}` : `slot-${idx}`}
                                    slot={slot}
                                    index={idx}
                                    totalSlots={multiViewSlots.length}
                                />
                            ))}

                            {/* Render empty placeholder card when 3 slots are active */}
                            {multiViewSlots.length === 3 && (
                                <Box className={styles.emptySlotCard} onClick={handleAddSlot}>
                                    <Box className={styles.emptySlotIcon}>
                                        <AddIcon sx={{ color: "#fff", fontSize: 28 }} />
                                    </Box>
                                    <Typography className={styles.emptySlotText}>
                                        {t("Sports.addEvent") || "+ Add Event"}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <div
                        className={styles.modalContentPlayer}
                        onClick={shouldEnableTapToggle ? togglePlayState : undefined}
                    >
                        <ReactPlayer
                            key={`${videoData?.id ?? "no-id"}-${isEventNotStartedVisible ? "overlay" : "stream"}-${isLiveType ? liveReloadNonce : 0}`}
                            ref={playerRef}
                            url={playerUrl}
                            playing={play && !isEventNotStartedVisible}
                            loop={isLiveType}
                            width="100%"
                            height="100%"
                            className={styles.videoSubtitles}
                            playsinline
                            muted={muted}
                            onProgress={handleProgress}
                            onDuration={handleDuration}
                            onEnded={handleClose}
                            onStart={() => setMuted(false)}
                            onLoad={() => setLoading(false)}
                            onReady={handleReady}
                            onError={() => setLoading(false)}
                            config={PlayerConfig}
                        />

                        <PauseOverlay
                            play={play}
                            isEventNotStartedVisible={!!isEventNotStartedVisible}
                            onToggle={togglePlayState}
                        />

                        <EventNotStartedOverlay
                            overlay={eventNotStartedOverlay ?? { visible: false }}
                            isSportsContext={isSportsContext}
                            onBack={handleBack}
                        />
                    </div>
                )}

                {!isMultiViewActive && (loading || !playerUrl) && (
                    <Loader className={styles.loader} />
                )}

                {!isMultiViewActive && (
                    <VideoPlayerControls
                        togglePlay={togglePlayState}
                        toggleFullscreen={toggleFullscreen}
                        handleClose={handleClose}
                        handleSeek={handleSeek}
                        handleLanguageChange={handlePersistedLanguageChange}
                        handleSubtitleChange={handleSubtitleChange}
                        handleSubtitleToggle={handleSubtitleToggle}
                        availableLanguages={availableLanguages}
                        availableSubtitles={availableSubtitles}
                        selectedLanguage={selectedLanguage}
                        selectedSubtitle={selectedSubtitle}
                        subtitlesEnabled={subtitlesEnabled}
                        onAirPlay={handleAirPlay}
                        isIPhone={isIOSIPhoneRef.current}
                    />
                )}
            </div>
        </Modal>
    );
};

export default VideoPlayer;

